import React, { useState, useEffect, useContext, useRef } from 'react'
import { connect } from 'react-redux'
import PropTypes from 'prop-types'
import createAuth0Client from '@auth0/auth0-spa-js'
import * as fromActions from './actions'
import { setAuthClient } from './auth'
import Cookies from 'js-cookie'
import ReactGA from 'react-ga4'

const defaultRedirect = () => {
  let dr = window.history.replaceState(
    {},
    document.title,
    window.location.pathname
  )
  console.log('defaultRedirect dr', dr)
  return dr
}

export const AuthContext = React.createContext()

export const useAuth = () => useContext(AuthContext)

const AuthProvider = ({
  children,
  onAuthRequested,
  onLoggedIn,
  onLoggedOut,
  onRedirectCallback = defaultRedirect,
  history,
  ...initOptions
}) => {

  const [isAuthenticated, setIsAuthenticated] = useState()
  const [user, setUser] = useState()
  const [authClient, setAuth0] = useState()
  const [loading, setLoading] = useState(true)
  const [popupOpen, setPopupOpen] = useState(false)
  const authClientInitPromiseRef = useRef(null)
  const sessionCheckFailureCountRef = useRef(0)
  const publicPaths = ['/verify', '/verifydriver', '/fbt-web-form', '/fbt-web-form/form/', '/fbt-web-form/form/add-submission', '/fbt-web-form/form/view-submissions']

  const getAuthClient = async () => {
    if (authClient) {
      return authClient
    }

    if (!authClientInitPromiseRef.current) {
      authClientInitPromiseRef.current = createAuth0Client(initOptions)
        .then((client) => {
          setAuth0(client)
          setAuthClient(client)
          return client
        })
        .finally(() => {
          authClientInitPromiseRef.current = null
        })
    }

    return authClientInitPromiseRef.current
  }

  const reportAuthError = (error, action) => {
    console.error('Auth Error:', error, 'Action:', action)
    if (window.location.host.includes('toyotafleet.app') && window.rollbar && typeof window.rollbar.error === 'function') {
      window.rollbar.error(error instanceof Error ? error : action, {
        action,
        path: window.location.pathname,
        search: window.location.search,
        authError: error?.error,
        authMessage: error?.message,
      })
    }

    ReactGA.event({
      category: 'User Session',
      action,
      label: error?.error || error?.message || 'Unknown error'
    })
  }

  const resetAuthState = () => {
    onLoggedOut()
    setIsAuthenticated(false)
    setUser(undefined)
  }

  const handleRedirectError = (error) => {
    reportAuthError(error, 'Login Callback Failed')
    window.history.replaceState({}, document.title, window.location.pathname)
    resetAuthState()
    setLoading(false)
    logout()
  }

  const handleAuthBootstrapError = (error) => {
    reportAuthError(error, 'Login Bootstrap Failed')
    resetAuthState()
    setLoading(false)
    logout()
  }

  const checkSessionValidity = async () => {
    let client = authClient

    if (!client) {
      try {
        client = await getAuthClient()
      }
      catch (error) {
        handleAuthBootstrapError(error)
        return
      }
    }

    if (publicPaths.includes(window.location.pathname))
    {return}

    client.getTokenSilently().then(() => {
      sessionCheckFailureCountRef.current = 0
    })
      .catch((e) => {
        if (e.error === 'login_required' || e.error === 'consent_required' || e.error === 'interaction_required') {
          logout()
          sessionCheckFailureCountRef.current += 1
          console.log('Session check failed, count:', sessionCheckFailureCountRef.current)

          if (sessionCheckFailureCountRef.current >= 2) {
            console.log('Session check failed twice, logging out user')
            logout()
          }
        }
      })
  }

  const logout = () => {
    localStorage.clear()
    localStorage.setItem('FORCE_LOGOUT_ALL_SESSIONS', true) // to logout other tab sessions if any
    window.location.href = '/logout'
  }

  useEffect(() => {

    const initAuth = async () => {
      try {
        onAuthRequested()
        const client = await getAuthClient()

        if (window.location.search.includes('code=') && window.location.search.includes('state=')) {
          if ( Cookies.get('auth0.cypress') === 'true') {
            const { appState } = { targetUrl: '/' }
            onRedirectCallback(appState)

          } else {
            try {
              const { appState } = await client.handleRedirectCallback()
              onRedirectCallback(appState)
              console.log('[GA] event tracked', { category: 'User Session', action: 'Login Success' })
              ReactGA.event({ category: 'User Session', action: 'Login Success' })
            }
            catch (error) {
              handleRedirectError(error)
              return
            }
          }
        }

        const authenticated = await client.isAuthenticated() || Cookies.get('auth0.cypress') === 'true'
        setIsAuthenticated(authenticated)

        if (authenticated) {
          let profile
          if (Cookies.get('auth0.cypress') === 'true') {
            profile = JSON.parse(Cookies.get('auth0.cypress.profile'))

          } else {
            profile = await client.getUser()
            profile.nickname = profile['https://schemas.toyotafleetportal.com/nickname'] ?? profile.nickname
          }

          setUser(profile)
          onLoggedIn(profile)
        }
        else {
          onLoggedOut()
        }

        setLoading(false)
      }
      catch (error) {
        handleAuthBootstrapError(error)
      }
    }

    initAuth()
  }, []) // eslint-disable-line

  const checkSessionValidityRef = useRef(checkSessionValidity)
  checkSessionValidityRef.current = checkSessionValidity

  useEffect(() => {
    const intervalId = setInterval(() => checkSessionValidityRef.current(), 60000)
    return () => clearInterval(intervalId)
  }, [])

  const loginWithPopup = async (params = {}) => {
    setPopupOpen(true)

    try {
      const client = await getAuthClient()
      await client.loginWithPopup(params)

      const profile = await client.getUser()
      profile.nickname = profile['https://schemas.toyotafleetportal.com/nickname'] ?? profile.nickname
      setUser(profile)
      setIsAuthenticated(true)
    }
    catch (error) {
      reportAuthError(error, 'Login Popup Failed')
    }
    finally {
      setPopupOpen(false)
    }
  }

  const handleRedirectCallback = async () => {
    setLoading(true)

    try {
      const client = await getAuthClient()
      await client.handleRedirectCallback()
      const profile = await client.getUser()
      profile.nickname = profile['https://schemas.toyotafleetportal.com/nickname'] ?? profile.nickname
      setLoading(false)
      setIsAuthenticated(true)
      setUser(profile)
    }
    catch (error) {
      handleRedirectError(error)
    }
  }

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        user,
        loading,
        popupOpen,
        loginWithPopup,
        handleRedirectCallback,
        getIdTokenClaims: async (...p) => {
          const client = await getAuthClient()
          return client.getIdTokenClaims(...p)
        },
        loginWithRedirect: async (...p) => {
          const client = await getAuthClient()
          return client.loginWithRedirect(...p)
        },
        getTokenSilently: async (...p) => {
          const client = await getAuthClient()
          return client.getTokenSilently(...p)
        },
        getTokenWithPopup: async (...p) => {
          const client = await getAuthClient()
          return client.getTokenWithPopup(...p)
        },
        logout: async (...p) => {
          const client = await getAuthClient()
          return client.logout(...p)
        }
      }}>
      {children}
    </AuthContext.Provider>
  )
}

AuthProvider.propTypes = {
  children: PropTypes.node,
  onAuthRequested: PropTypes.func.isRequired,
  onLoggedIn: PropTypes.func.isRequired,
  onLoggedOut: PropTypes.func.isRequired,
  onRedirectCallback: PropTypes.func,
}

const mapDispatchToProps = {
  onAuthRequested: fromActions.authRequested,
  onLoggedIn: fromActions.loggedIn,
  onLoggedOut: fromActions.loggedOut,
}

export default connect(
  null,
  mapDispatchToProps
)(AuthProvider)
